import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import Snackbar from "@material-ui/core/Snackbar";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import SelectSearchSingle from "components/Filters/SelectSearchSingle.jsx";
import SelectSearchMultiple from "components/Filters/SelectSearchMultiple.jsx";

import api from "api/index";

// ── Brand ─────────────────────────────────────────────────────────────────────
const NAVY      = "#1a237e";
const NAVY_DARK = "#0d1b6e";

// ── Data ─────────────────────────────────────────────────────────────────────
const ROLES = ["Principal", "Registrar", "Bursar", "Dean of Studies", "Lecturer", "Librarian", "Administrator"];

const DEPARTMENTS = ["Academic Affairs", "Finance", "Library", "Human Resource", "Student Affairs", "Administration"];

const MODULE_TREE = [
  {
    key: "ACADEMICS", label: "Academics",
    submodules: [
      { key: "ADMISSIONS",  label: "Admissions",      actions: ["VIEW", "PROCESS", "APPROVE"] },
      { key: "STUDENTS",    label: "Students",         actions: ["VIEW", "CREATE", "EDIT", "DELETE"] },
      { key: "CURRICULUM",  label: "Curriculum",       actions: ["VIEW", "EDIT"] },
      { key: "TIMETABLE",   label: "Timetable",        actions: ["VIEW", "MANAGE"] },
      { key: "ENROLLMENT",  label: "Enrollment",       actions: ["VIEW", "ENROLL", "DROP"] },
      { key: "EXAMS",       label: "Exams",            actions: ["VIEW", "SCHEDULE"] },
      { key: "RESULTS",     label: "Results",          actions: ["VIEW", "ENTER", "APPROVE"] },
      { key: "CLEARANCE",   label: "Clearance",        actions: ["VIEW", "APPROVE", "REJECT"] },
    ]
  },
  {
    key: "LIBRARY", label: "Library",
    submodules: [
      { key: "CATALOGUE",   label: "Catalogue",        actions: ["VIEW", "MANAGE"] },
      { key: "BOOK_ISSUES", label: "Issues & Returns", actions: ["VIEW", "ISSUE", "RETURN"] },
    ]
  },
  {
    key: "FINANCE", label: "Finance",
    submodules: [
      { key: "FEES",        label: "Fees",        actions: ["VIEW", "COLLECT", "MANAGE"] },
      { key: "BUDGET",      label: "Budget",      actions: ["VIEW", "MANAGE"] },
      { key: "EXPENDITURE", label: "Expenditure", actions: ["VIEW", "RECORD"] },
    ]
  },
  {
    key: "HUMAN_RESOURCE", label: "Human Resource",
    submodules: [
      { key: "STAFF",       label: "Staff Records", actions: ["VIEW", "ADD", "EDIT"] },
      { key: "LECTURERS",   label: "Lecturers",     actions: ["VIEW", "ASSIGN"] },
      { key: "LEAVE",       label: "Leave",         actions: ["VIEW", "APPROVE"] },
      { key: "PAYROLL",     label: "Payroll",       actions: ["VIEW", "PROCESS", "APPROVE"] },
    ]
  },
  {
    key: "PROCUREMENT", label: "Procurement",
    submodules: [
      { key: "PURCHASE_ORDERS", label: "Purchase Orders", actions: ["VIEW", "CREATE", "APPROVE"] },
      { key: "INVENTORY",       label: "Inventory",       actions: ["VIEW", "MANAGE"] },
      { key: "ASSETS",          label: "Assets",          actions: ["VIEW", "MANAGE"] },
    ]
  },
  {
    key: "STUDENT_AFFAIRS", label: "Student Affairs",
    submodules: [
      { key: "GUILD",    label: "Guild / Council", actions: ["VIEW", "MANAGE"] },
      { key: "CATERING", label: "Catering",        actions: ["VIEW", "MANAGE"] },
      { key: "ALUMNI",   label: "Alumni",          actions: ["VIEW", "MANAGE"] },
    ]
  },
  {
    key: "ADMINISTRATION", label: "Administration",
    submodules: [
      { key: "USERS",       label: "Users",       actions: ["VIEW", "CREATE", "EDIT", "DELETE"] },
      { key: "PERMISSIONS", label: "Permissions", actions: ["VIEW", "MANAGE"] },
      { key: "REPORTS",     label: "Reports",     actions: ["VIEW", "EXPORT"] },
      { key: "AUDIT",       label: "Audit Log",   actions: ["VIEW"] },
    ]
  }
];

const permKey = (mod, sub) => `${mod}.${sub}`;

// ── Styles ─────────────────────────────────────────────────────────────────────
const styles = {
  page: { background: "#f4f5f7", minHeight: "100vh", padding: "0" },

  /* breadcrumb bar */
  breadBar: {
    display: "flex", alignItems: "center", gap: "6px",
    fontSize: "12px", color: "#666",
    padding: "10px 0 6px"
  },
  breadHome: { display: "flex", alignItems: "center", gap: "4px", color: NAVY, fontWeight: 600, cursor: "pointer" },
  breadSep: { color: "#bbb" },
  breadCurrent: { color: "#444" },

  /* tab strip */
  tabStrip: {
    display: "flex",
    borderBottom: "2px solid #e0e0e0",
    marginBottom: "0",
    background: "#fff",
    paddingLeft: "2px"
  },
  tab: {
    padding: "10px 22px",
    fontSize: "13px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#888",
    cursor: "pointer",
    borderBottom: "3px solid transparent",
    marginBottom: "-2px",
    userSelect: "none",
    transition: "color 150ms"
  },
  tabActive: {
    color: NAVY,
    borderBottomColor: NAVY
  },

  /* action button row */
  actionRow: {
    display: "flex", gap: "8px", flexWrap: "wrap",
    padding: "14px 0 16px"
  },
  btnFilled: {
    padding: "7px 18px",
    background: `linear-gradient(90deg, ${NAVY_DARK}, ${NAVY})`,
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "opacity 150ms"
  },
  btnOutlined: {
    padding: "6px 18px",
    background: "transparent",
    color: "#555",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "border-color 150ms, color 150ms"
  },
  btnActive: {
    color: NAVY,
    borderColor: NAVY
  },

  /* main card */
  card: {
    background: "#fff",
    borderRadius: "4px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
    overflow: "hidden"
  },

  /* info bar */
  infoBar: {
    display: "flex", alignItems: "flex-start", gap: "10px",
    background: "#f8f9fa",
    border: "1px solid #e8eaf6",
    borderLeft: `4px solid ${NAVY}`,
    borderRadius: "4px",
    padding: "12px 16px",
    margin: "20px 0 24px",
    fontSize: "13px",
    color: "#444"
  },
  infoIcon: { color: NAVY, marginTop: "1px", flexShrink: 0 },

  /* form grid */
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px 28px",
    marginBottom: "24px",
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" }
  },
  fieldWrap: {},
  fieldLabel: {
    fontSize: "11px", fontWeight: 700, color: "#64748b",
    textTransform: "uppercase", letterSpacing: "0.5px",
    marginBottom: "6px", display: "block"
  },

  /* action bar below form */
  formActions: {
    display: "flex", justifyContent: "flex-end", gap: "10px",
    paddingTop: "4px", borderTop: "1px solid #f0f0f0", marginTop: "8px"
  },

  /* module tree */
  treeWrap: { marginTop: "12px" },
  treeGroup: { marginBottom: "8px", border: "1px solid #e8eaf6", borderRadius: "6px", overflow: "hidden" },
  treeGroupHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "9px 14px", cursor: "pointer",
    background: `linear-gradient(90deg, ${NAVY_DARK}, ${NAVY})`,
    color: "#fff", fontWeight: 700, fontSize: "12px", userSelect: "none",
    textTransform: "uppercase", letterSpacing: "0.4px"
  },
  treeSubRow: {
    display: "flex", alignItems: "center",
    padding: "6px 14px", borderBottom: "1px solid #f4f5f7",
    fontSize: "13px"
  },
  treeSubLabel: { width: "180px", flexShrink: 0, fontWeight: 500, color: "#3C4858" },
  treeActions: { display: "flex", flexWrap: "wrap", gap: "4px", flex: 1 },
  chip: {
    display: "inline-flex", alignItems: "center",
    padding: "2px 10px", borderRadius: "12px",
    fontSize: "10px", fontWeight: 700,
    border: `1px solid ${NAVY}`,
    cursor: "pointer", userSelect: "none",
    transition: "all 0.12s"
  },
  chipOn: { background: NAVY, color: "#fff" },
  chipOff: { background: "transparent", color: NAVY },

  saveRow: { display: "flex", justifyContent: "flex-end", padding: "16px 0 4px" },

  emptyState: { textAlign: "center", color: "#9e9e9e", padding: "32px", fontSize: "13px" },

  /* staff list */
  staffTable: { width: "100%", borderCollapse: "collapse", marginBottom: "20px", fontSize: "13px" },
  staffTh: { textAlign: "left", padding: "8px 12px", background: NAVY, color: "#fff", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" },
  staffTd: { padding: "8px 12px", borderBottom: "1px solid #f0f0f0" },
  staffRowSelected: { background: "#e8eaf6" },

  /* permission matrix */
  matrixWrap: { overflowX: "auto", marginTop: "12px" },
  matrixTh: { padding: "8px 10px", background: NAVY, color: "#fff", fontSize: "11px", fontWeight: 700, textAlign: "center", minWidth: "90px" },
  matrixThFirst: { textAlign: "left", minWidth: "160px" },
  matrixTd: { padding: "4px 10px", borderBottom: "1px solid #eee", textAlign: "center" },
  matrixTdFirst: { textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #eee", fontWeight: 600, fontSize: "13px" }
};

// ── ActionChip ─────────────────────────────────────────────────────────────────
function ActionChip({ action, selected, onToggle, readonly }) {
  return (
    <span
      onClick={readonly ? undefined : onToggle}
      style={{
        display: "inline-flex", alignItems: "center",
        padding: "2px 10px", borderRadius: "12px",
        fontSize: "10px", fontWeight: 700,
        border: `1px solid ${NAVY}`,
        cursor: readonly ? "default" : "pointer",
        userSelect: "none", transition: "all 0.12s",
        background: selected ? NAVY : "transparent",
        color: selected ? "#fff" : NAVY,
        margin: "2px"
      }}
    >
      {action}
    </span>
  );
}

// ── ModuleTree ─────────────────────────────────────────────────────────────────
function ModuleTree({ perms, onToggleAction, onToggleAll, readonly }) {
  const [open, setOpen] = React.useState(() => {
    const s = {};
    MODULE_TREE.forEach(m => { s[m.key] = true; });
    return s;
  });

  return (
    <div>
      {MODULE_TREE.map(mod => {
        const isOpen = open[mod.key];
        const allChecked = mod.submodules.every(sub =>
          sub.actions.every(a => (perms[permKey(mod.key, sub.key)] || []).includes(a))
        );
        const anyChecked = mod.submodules.some(sub =>
          (perms[permKey(mod.key, sub.key)] || []).length > 0
        );
        return (
          <div key={mod.key} style={{ marginBottom: "8px", border: "1px solid #e8eaf6", borderRadius: "6px", overflow: "hidden" }}>
            <div
              onClick={() => setOpen(s => ({ ...s, [mod.key]: !s[mod.key] }))}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "9px 14px", cursor: "pointer",
                background: `linear-gradient(90deg, ${NAVY_DARK}, ${NAVY})`,
                color: "#fff", fontWeight: 700, fontSize: "12px",
                userSelect: "none", textTransform: "uppercase", letterSpacing: "0.4px"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {!readonly && (
                  <Checkbox
                    checked={allChecked}
                    indeterminate={!allChecked && anyChecked}
                    onClick={e => e.stopPropagation()}
                    onChange={() => onToggleAll && onToggleAll(mod.key, !allChecked)}
                    style={{ padding: "2px", color: "#fff" }}
                    size="small"
                  />
                )}
                {mod.label}
                {anyChecked && !allChecked && <span style={{ fontSize: "10px", opacity: 0.65, fontWeight: 400 }}>(partial)</span>}
                {allChecked && <span style={{ fontSize: "10px", opacity: 0.65, fontWeight: 400 }}>(full)</span>}
              </div>
              {isOpen ? <ExpandLessIcon style={{ fontSize: "16px" }} /> : <ExpandMoreIcon style={{ fontSize: "16px" }} />}
            </div>

            {isOpen && (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#e8eaf6" }}>
                    <th style={{ fontSize: "11px", fontWeight: 700, padding: "6px 14px", color: NAVY, width: "200px", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.3px" }}>Submodule</th>
                    <th style={{ fontSize: "11px", fontWeight: 700, padding: "6px 12px", color: NAVY, textAlign: "left", textTransform: "uppercase", letterSpacing: "0.3px" }}>Actions</th>
                    {!readonly && <th style={{ fontSize: "11px", fontWeight: 700, padding: "6px 8px", color: NAVY, width: "70px", textAlign: "center", textTransform: "uppercase", letterSpacing: "0.3px" }}>All</th>}
                  </tr>
                </thead>
                <tbody>
                  {mod.submodules.map(sub => {
                    const k = permKey(mod.key, sub.key);
                    const current = perms[k] || [];
                    const allSub = sub.actions.every(a => current.includes(a));
                    const anySub = sub.actions.some(a => current.includes(a));
                    return (
                      <tr key={sub.key}>
                        <td style={{ fontSize: "13px", padding: "6px 14px", borderBottom: "1px solid #f4f5f7", fontWeight: 500 }}>{sub.label}</td>
                        <td style={{ padding: "4px 12px", borderBottom: "1px solid #f4f5f7" }}>
                          {sub.actions.map(action => (
                            <ActionChip key={action} action={action} selected={current.includes(action)} readonly={readonly}
                              onToggle={readonly ? undefined : () => onToggleAction(mod.key, sub.key, action)} />
                          ))}
                        </td>
                        {!readonly && (
                          <td style={{ padding: "4px 8px", borderBottom: "1px solid #f4f5f7", textAlign: "center" }}>
                            <Tooltip title={allSub ? "Remove all" : "Grant all"}>
                              <Checkbox checked={allSub} indeterminate={!allSub && anySub}
                                onChange={() => onToggleAll && onToggleAll(mod.key, !allChecked, sub.key)}
                                style={{ padding: "2px", color: NAVY }} size="small" />
                            </Tooltip>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Matrix defaults ────────────────────────────────────────────────────────────
const MATRIX_DEFAULTS = {
  "Students":       { Principal: true, Registrar: true, Bursar: "view", "Dean of Studies": true, Lecturer: "view", Librarian: false, Administrator: true },
  "Finance / Fees": { Principal: true, Registrar: "view", Bursar: true, "Dean of Studies": "view", Lecturer: false, Librarian: false, Administrator: true },
  "Timetable":      { Principal: true, Registrar: true, Bursar: false, "Dean of Studies": true, Lecturer: "view", Librarian: false, Administrator: true },
  "Results":        { Principal: true, Registrar: true, Bursar: false, "Dean of Studies": true, Lecturer: true, Librarian: false, Administrator: true },
  "Library":        { Principal: true, Registrar: false, Bursar: false, "Dean of Studies": "view", Lecturer: "view", Librarian: true, Administrator: true },
  "HR / Staff":     { Principal: true, Registrar: "view", Bursar: "view", "Dean of Studies": false, Lecturer: false, Librarian: false, Administrator: true },
  "Reports":        { Principal: true, Registrar: true, Bursar: true, "Dean of Studies": true, Lecturer: false, Librarian: false, Administrator: true },
  "Audit Log":      { Principal: true, Registrar: false, Bursar: false, "Dean of Studies": false, Lecturer: false, Librarian: false, Administrator: true },
};

// ── Permission levels ──────────────────────────────────────────────────────────
const PERMISSION_LEVELS = ["Read Only", "Read Write", "Full Rights"];

// ── Permission Sets helpers ────────────────────────────────────────────────────
function getSubmodules(moduleKey) {
  const mod = MODULE_TREE.find(m => m.key === moduleKey);
  return mod ? mod.submodules : [];
}

function getActions(moduleKey, subKey) {
  const mod = MODULE_TREE.find(m => m.key === moduleKey);
  if (!mod) return [];
  const sub = mod.submodules.find(s => s.key === subKey);
  return sub ? sub.actions : [];
}

// ── Main Component ─────────────────────────────────────────────────────────────
class PermissionsMgt extends Component {
  state = {
    tab: 0,
    action: "assign",

    /* USER PERMISSIONS */
    department: "",
    position: "",
    selectedUserId: "",
    selectedRoles: [],
    staffList: [],
    staffLoading: false,
    perms: {},
    permsLoading: false,
    permsSaving: false,
    permsLoaded: false,

    /* PERMISSION SETS */
    psSubTab: 0,          // 0=Position Permissions, 1=Module Permissions
    psModuleFilter: null,
    psSubFilter: null,
    psSearch: "",
    psShowPanel: false,
    psOpenMods: {},
    psOpenSubs: {},
    // add-form
    psNewModule: null,
    psNewSub: null,
    psNewProcess: null,
    psNewPermission: null,
    // entries list (local, also loaded from API)
    psEntries: [],
    psEntriesSaving: false,
    // position perms (sub-tab 0)
    byPosRole: "Principal",
    byPosPerms: {},
    byPosLoading: false,
    byPosSaving: false,
    byPosLoaded: false,

    /* MATRIX */
    matrix: JSON.parse(JSON.stringify(MATRIX_DEFAULTS)),
    matrixDirty: false,

    snack: false,
    snackMsg: "",
    snackOk: true
  };

  componentDidMount() { this.loadStaff(); }

  loadStaff = async () => {
    this.setState({ staffLoading: true });
    try {
      const res = await api.get("/staff", { params: { limit: 100 } });
      const d = res.data.data || res.data;
      this.setState({ staffList: Array.isArray(d) ? d : d.data || [], staffLoading: false });
    } catch { this.setState({ staffLoading: false }); }
  };

  loadUserPerms = async userId => {
    if (!userId) return;
    this.setState({ permsLoading: true, permsLoaded: false, perms: {} });
    try {
      const res = await api.get(`/permissions/user/${userId}`);
      const rows = res.data.data || [];
      const p = {};
      rows.forEach(r => { p[permKey(r.module, r.submodule)] = r.actions || []; });
      this.setState({ perms: p, permsLoading: false, permsLoaded: true });
    } catch { this.setState({ permsLoading: false, permsLoaded: true }); }
  };

  saveUserPerms = async () => {
    const { selectedUserId, perms } = this.state;
    if (!selectedUserId) return;
    this.setState({ permsSaving: true });
    try {
      const permissions = Object.entries(perms).filter(([, v]) => v.length).map(([k, actions]) => {
        const [module, submodule] = k.split(".");
        return { module, submodule, actions };
      });
      await api.post("/permissions/user", { staffId: selectedUserId, permissions });
      this.setState({ permsSaving: false, snack: true, snackMsg: "User permissions saved.", snackOk: true });
    } catch (e) {
      this.setState({ permsSaving: false, snack: true, snackMsg: e.response?.data?.message || "Failed to save.", snackOk: false });
    }
  };

  loadByPosPerms = async () => {
    const { byPosRole } = this.state;
    this.setState({ byPosLoading: true, byPosLoaded: false });
    try {
      const res = await api.get(`/permissions/position/${encodeURIComponent(byPosRole)}`);
      const rows = res.data.data || [];
      const p = {};
      rows.forEach(r => { p[permKey(r.module, r.submodule)] = r.actions || []; });
      this.setState({ byPosPerms: p, byPosLoading: false, byPosLoaded: true });
    } catch { this.setState({ byPosPerms: {}, byPosLoading: false, byPosLoaded: true }); }
  };

  saveByPosPerms = async () => {
    const { byPosRole, byPosPerms } = this.state;
    this.setState({ byPosSaving: true });
    try {
      const permissions = Object.entries(byPosPerms).filter(([, v]) => v.length).map(([k, actions]) => {
        const [module, submodule] = k.split(".");
        return { module, submodule, actions };
      });
      await api.post("/permissions/position", { role: byPosRole, permissions });
      this.setState({ byPosSaving: false, snack: true, snackMsg: `Saved for ${byPosRole}.`, snackOk: true });
    } catch (e) {
      this.setState({ byPosSaving: false, snack: true, snackMsg: e.response?.data?.message || "Failed.", snackOk: false });
    }
  };

  toggleAction = (mk, sk, action) => {
    const k = permKey(mk, sk);
    const current = [...(this.state.perms[k] || [])];
    const idx = current.indexOf(action);
    if (idx >= 0) current.splice(idx, 1); else current.push(action);
    this.setState(prev => ({ perms: { ...prev.perms, [k]: current } }));
  };
  toggleAll = (mk, full, sk) => {
    const mod = MODULE_TREE.find(m => m.key === mk);
    if (!mod) return;
    this.setState(prev => {
      const next = { ...prev.perms };
      if (sk) {
        const sub = mod.submodules.find(s => s.key === sk);
        if (!sub) return {};
        const k = permKey(mk, sk);
        const allNow = sub.actions.every(a => (next[k] || []).includes(a));
        next[k] = allNow ? [] : [...sub.actions];
      } else {
        mod.submodules.forEach(sub => {
          next[permKey(mk, sub.key)] = full ? [...sub.actions] : [];
        });
      }
      return { perms: next };
    });
  };

  toggleByPosAction = (mk, sk, action) => {
    const k = permKey(mk, sk);
    const current = [...(this.state.byPosPerms[k] || [])];
    const idx = current.indexOf(action);
    if (idx >= 0) current.splice(idx, 1); else current.push(action);
    this.setState(prev => ({ byPosPerms: { ...prev.byPosPerms, [k]: current } }));
  };
  toggleByPosAll = (mk, full, sk) => {
    const mod = MODULE_TREE.find(m => m.key === mk);
    if (!mod) return;
    this.setState(prev => {
      const next = { ...prev.byPosPerms };
      if (sk) {
        const sub = mod.submodules.find(s => s.key === sk);
        if (!sub) return {};
        const k = permKey(mk, sk);
        const allNow = sub.actions.every(a => (next[k] || []).includes(a));
        next[k] = allNow ? [] : [...sub.actions];
      } else {
        mod.submodules.forEach(sub => {
          next[permKey(mk, sub.key)] = full ? [...sub.actions] : [];
        });
      }
      return { byPosPerms: next };
    });
  };

  cycleMatrix = (mod, role) => {
    const cur = this.state.matrix[mod][role];
    const next = cur === true ? "view" : cur === "view" ? false : true;
    const m = JSON.parse(JSON.stringify(this.state.matrix));
    m[mod][role] = next;
    this.setState({ matrix: m, matrixDirty: true });
  };

  psAddEntry = () => {
    const { psNewModule, psNewSub, psNewProcess, psNewPermission, psEntries } = this.state;
    if (!psNewModule || !psNewSub || !psNewProcess || !psNewPermission) return;
    const dupe = psEntries.find(e =>
      e.module === psNewModule && e.submodule === psNewSub && e.process === psNewProcess
    );
    if (dupe) return;
    const entry = {
      id: Date.now(),
      module: psNewModule, moduleName: MODULE_TREE.find(m => m.key === psNewModule)?.label || psNewModule,
      submodule: psNewSub, submoduleName: getSubmodules(psNewModule).find(s => s.key === psNewSub)?.label || psNewSub,
      process: psNewProcess,
      permission: psNewPermission
    };
    this.setState({
      psEntries: [...psEntries, entry],
      psShowPanel: false,
      psNewModule: null, psNewSub: null, psNewProcess: null, psNewPermission: null,
      snack: true, snackMsg: "Permission entry added.", snackOk: true
    });
  };

  psRemoveEntry = id => {
    this.setState(prev => ({ psEntries: prev.psEntries.filter(e => e.id !== id) }));
  };

  render() {
    const { classes } = this.props;
    const {
      tab, action,
      department, position, selectedUserId, staffList, staffLoading,
      perms, permsLoading, permsSaving, permsLoaded,
      matrix, matrixDirty,
      snack, snackMsg, snackOk
    } = this.state;

    const selectedStaff = staffList.find(s => String(s.id) === String(selectedUserId));
    const filteredStaff = staffList.filter(s => {
      if (department && s.departmentName !== department) return false;
      if (position && s.role !== position) return false;
      return true;
    });

    return (
      <div className={classes.page}>
        <GridContainer>
          <GridItem xs={12}>

            {/* Breadcrumb */}
            <div className={classes.breadBar}>
              <span className={classes.breadHome}>
                <Icon style={{ fontSize: "14px" }}>home</Icon> Home
              </span>
              <span className={classes.breadSep}>&rsaquo;</span>
              <Icon style={{ fontSize: "14px", color: "#888" }}>lock</Icon>
              <span className={classes.breadCurrent}>Permissions</span>
            </div>

            {/* Tab strip */}
            <div className={classes.tabStrip}>
              {["USER PERMISSIONS", "PERMISSION SETS", "PERMISSION MATRIX"].map((t, i) => (
                <div
                  key={i}
                  className={`${classes.tab} ${tab === i ? classes.tabActive : ""}`}
                  onClick={() => this.setState({ tab: i })}
                >
                  {t}
                </div>
              ))}
            </div>

            {/* ── TAB 0: USER PERMISSIONS ── */}
            {tab === 0 && (
              <>
                {/* Action button row */}
                <div className={classes.actionRow}>
                  {[
                    { key: "assign",      label: "Assign Access" },
                    { key: "unassign",    label: "Unassign Access" },
                    { key: "multi",       label: "Multi Assign" },
                    { key: "by_position", label: "Assign by Position" },
                  ].map(btn => (
                    <button
                      key={btn.key}
                      onClick={() => this.setState({ action: btn.key, permsLoaded: false, perms: {}, selectedUserId: "" })}
                      className={action === btn.key ? classes.btnFilled : `${classes.btnOutlined} ${action === btn.key ? classes.btnActive : ""}`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>

                <div className={classes.card} style={{ padding: "20px 24px" }}>

                  {/* Info bar */}
                  <div className={classes.infoBar}>
                    <Icon className={classes.infoIcon} style={{ fontSize: "18px" }}>info</Icon>
                    <span>
                      {action === "assign"      && "Assign a user permissions. Tick to assign and untick to unassign permission."}
                      {action === "unassign"    && "Remove permissions from a specific user."}
                      {action === "multi"       && "Assign the same permissions to multiple users at once."}
                      {action === "by_position" && "Assign permissions to all users holding a specific position/role."}
                    </span>
                  </div>

                  {/* Form grid */}
                  <div className={classes.formGrid}>

                    <div className={classes.fieldWrap}>
                      <SelectSearchSingle
                        label="Department"
                        placeholder="Choose Department"
                        name="department"
                        value={department || null}
                        suggestions={DEPARTMENTS.map(d => ({ value: d, label: d }))}
                        getSearchValue={(val) => this.setState({ department: val || "", selectedUserId: "", permsLoaded: false })}
                      />
                    </div>

                    <div className={classes.fieldWrap}>
                      <SelectSearchSingle
                        label="Position"
                        placeholder="Search & Select Position"
                        name="position"
                        value={position || null}
                        suggestions={ROLES.map(r => ({ value: r, label: r }))}
                        getSearchValue={(val) => this.setState({ position: val || "", selectedUserId: "", permsLoaded: false })}
                      />
                    </div>

                    <div className={classes.fieldWrap}>
                      <SelectSearchSingle
                        label="User"
                        placeholder="Search & Select User"
                        name="user"
                        value={selectedUserId || null}
                        suggestions={filteredStaff.map(s => ({
                          value: String(s.id),
                          label: `${`${s.firstName || ""} ${s.lastName || ""}`.trim() || s.staffNo}${s.role ? ` — ${s.role}` : ""}`
                        }))}
                        getSearchValue={(val) => {
                          this.setState({ selectedUserId: val || "" }, () => {
                            if (val) this.loadUserPerms(val);
                          });
                        }}
                      />
                    </div>

                    <div className={classes.fieldWrap}>
                      <SelectSearchMultiple
                        label="Choose Role(s)"
                        placeholder="Choose Role(s)"
                        name="roles"
                        values={this.state.selectedRoles}
                        suggestions={ROLES.map(r => ({ value: r, label: r }))}
                        getSearchValues={(vals) => this.setState({ selectedRoles: vals || [] })}
                      />
                    </div>
                  </div>

                  {/* Module access tree */}
                  {permsLoading && (
                    <div style={{ textAlign: "center", padding: "30px" }}><CircularProgress style={{ color: NAVY }} /></div>
                  )}

                  {!selectedUserId && !permsLoading && (
                    <div className={classes.emptyState}>
                      Select a user above to configure their module access permissions.
                    </div>
                  )}

                  {permsLoaded && selectedUserId && !permsLoading && (
                    <>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                        <p style={{ margin: 0, fontSize: "13px", color: "#555" }}>
                          Configuring: <strong style={{ color: NAVY }}>
                            {selectedStaff ? `${selectedStaff.firstName || ""} ${selectedStaff.lastName || ""}`.trim() : "Selected User"}
                          </strong>
                        </p>
                      </div>
                      <ModuleTree perms={perms} onToggleAction={this.toggleAction} onToggleAll={this.toggleAll} />
                      <div className={classes.saveRow}>
                        <button
                          className={classes.btnFilled}
                          onClick={this.saveUserPerms}
                          disabled={permsSaving}
                          style={permsSaving ? { opacity: 0.7 } : {}}
                        >
                          {permsSaving
                            ? <CircularProgress size={14} style={{ color: "#fff", marginRight: "6px", verticalAlign: "middle" }} />
                            : null}
                          Save Permissions
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* ── TAB 1: PERMISSION SETS ── */}
            {tab === 1 && (() => {
              const {
                psSubTab, psModuleFilter, psSubFilter, psSearch, psShowPanel, psOpenMods, psOpenSubs,
                psNewModule, psNewSub, psNewProcess, psNewPermission, psEntries,
                byPosRole, byPosPerms, byPosLoading, byPosSaving, byPosLoaded
              } = this.state;

              // filtered entries
              const visibleEntries = psEntries.filter(e => {
                if (psModuleFilter && e.module !== psModuleFilter) return false;
                if (psSubFilter && e.submodule !== psSubFilter) return false;
                if (psSearch) {
                  const q = psSearch.toLowerCase();
                  if (!e.moduleName.toLowerCase().includes(q) && !e.submoduleName.toLowerCase().includes(q) && !e.process.toLowerCase().includes(q)) return false;
                }
                return true;
              });

              // group by module
              const grouped = {};
              visibleEntries.forEach(e => {
                if (!grouped[e.module]) grouped[e.module] = { label: e.moduleName, subs: {} };
                if (!grouped[e.module].subs[e.submodule]) grouped[e.module].subs[e.submodule] = { label: e.submoduleName, entries: [] };
                grouped[e.module].subs[e.submodule].entries.push(e);
              });

              const subActions = psNewModule ? getActions(psNewModule, psNewSub) : [];

              return (
                <>
                  {/* Sub-tab row + ADD MODULE SET button */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0 4px" }}>
                    <div style={{ display: "flex", gap: "2px" }}>
                      {["POSITION PERMISSIONS", "MODULE PERMISSIONS"].map((t, i) => (
                        <button
                          key={i}
                          onClick={() => this.setState({ psSubTab: i })}
                          className={psSubTab === i ? classes.btnFilled : classes.btnOutlined}
                          style={{ borderRadius: "4px 4px 0 0", marginBottom: 0 }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {psSubTab === 1 && (
                      <button
                        className={classes.btnOutlined}
                        style={{ display: "flex", alignItems: "center", gap: "4px" }}
                        onClick={() => this.setState({ psShowPanel: true })}
                      >
                        <Icon style={{ fontSize: "16px" }}>playlist_add</Icon> ADD MODULE SET
                      </button>
                    )}
                  </div>

                  {/* ── Sub-tab 0: Position Permissions ── */}
                  {psSubTab === 0 && (
                    <div className={classes.card} style={{ padding: "20px 24px" }}>
                      <div className={classes.infoBar}>
                        <Icon className={classes.infoIcon} style={{ fontSize: "18px" }}>info</Icon>
                        <span>Configure module access for an entire position/role. Applies to all staff holding the selected role.</span>
                      </div>
                      <div style={{ maxWidth: "320px", marginBottom: "20px" }}>
                        <SelectSearchSingle
                          label="Select Position / Role"
                          placeholder="Search & Select Position"
                          name="byPosRole"
                          value={byPosRole || null}
                          suggestions={ROLES.map(r => ({ value: r, label: r }))}
                          getSearchValue={val => { if (val) this.setState({ byPosRole: val, byPosLoaded: false, byPosPerms: {} }); }}
                        />
                      </div>
                      <div style={{ marginBottom: "16px" }}>
                        <button className={classes.btnFilled} onClick={this.loadByPosPerms} disabled={byPosLoading}>
                          {byPosLoading && <CircularProgress size={13} style={{ color: "#fff", marginRight: "6px", verticalAlign: "middle" }} />}
                          Load Permissions
                        </button>
                      </div>
                      {byPosLoading && <div style={{ textAlign: "center", padding: "30px" }}><CircularProgress style={{ color: NAVY }} /></div>}
                      {!byPosLoaded && !byPosLoading && <div className={classes.emptyState}>Select a position and click "Load Permissions".</div>}
                      {byPosLoaded && !byPosLoading && (
                        <>
                          <p style={{ fontSize: "13px", color: "#555", margin: "0 0 12px" }}>
                            Configuring: <strong style={{ color: NAVY }}>{byPosRole}</strong>
                          </p>
                          <ModuleTree perms={byPosPerms} onToggleAction={this.toggleByPosAction} onToggleAll={this.toggleByPosAll} />
                          <div className={classes.saveRow}>
                            <button className={classes.btnFilled} onClick={this.saveByPosPerms} disabled={byPosSaving} style={byPosSaving ? { opacity: 0.7 } : {}}>
                              {byPosSaving && <CircularProgress size={13} style={{ color: "#fff", marginRight: "6px", verticalAlign: "middle" }} />}
                              Save Position Permissions
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* ── Sub-tab 1: Module Permissions (split panel) ── */}
                  {psSubTab === 1 && (
                    <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>

                      {/* LEFT: tree */}
                      <div className={classes.card} style={{ flex: 1, padding: "16px 20px", minWidth: 0 }}>
                        <div className={classes.infoBar} style={{ margin: "0 0 16px" }}>
                          <Icon className={classes.infoIcon} style={{ fontSize: "18px" }}>info</Icon>
                          <span>Configure permissions for each module. Removing a set deletes the permission for all users.</span>
                        </div>

                        {/* Filter chips + search */}
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
                          {psModuleFilter && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#e8eaf6", color: NAVY, borderRadius: "14px", padding: "3px 10px", fontSize: "12px", fontWeight: 600 }}>
                              Module
                              <Icon style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => this.setState({ psModuleFilter: null })}>close</Icon>
                            </span>
                          )}
                          {psSubFilter && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#e8eaf6", color: NAVY, borderRadius: "14px", padding: "3px 10px", fontSize: "12px", fontWeight: 600 }}>
                              Sub Module
                              <Icon style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => this.setState({ psSubFilter: null })}>close</Icon>
                            </span>
                          )}
                          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
                            <Icon style={{ fontSize: "16px", color: "#aaa" }}>search</Icon>
                            <input
                              type="text"
                              placeholder="Search..."
                              value={psSearch}
                              onChange={e => this.setState({ psSearch: e.target.value })}
                              style={{ border: "none", outline: "none", fontSize: "13px", color: "#555", fontFamily: "inherit", background: "transparent", width: "140px" }}
                            />
                            <Icon
                              style={{ fontSize: "16px", color: "#aaa", cursor: "pointer" }}
                              title="Toggle visibility"
                            >visibility_off</Icon>
                          </div>
                        </div>

                        {/* Column headers */}
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                              <th style={{ textAlign: "left", padding: "6px 10px", fontSize: "12px", fontWeight: 700, color: "#555", width: "36px" }}></th>
                              <th style={{ textAlign: "left", padding: "6px 10px", fontSize: "12px", fontWeight: 700, color: "#555" }}>Process</th>
                              <th style={{ textAlign: "left", padding: "6px 10px", fontSize: "12px", fontWeight: 700, color: "#555" }}>Permission</th>
                              <th style={{ textAlign: "left", padding: "6px 10px", fontSize: "12px", fontWeight: 700, color: "#555" }}>Action(s)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.keys(grouped).length === 0 ? (
                              <tr><td colSpan={4} style={{ textAlign: "center", padding: "32px", color: "#bbb", fontSize: "13px" }}>
                                No permission sets added yet. Click "ADD MODULE SET" to begin.
                              </td></tr>
                            ) : Object.entries(grouped).map(([modKey, modGroup]) => {
                              const modOpen = psOpenMods[modKey] !== false;
                              return (
                                <React.Fragment key={modKey}>
                                  {/* Module header row */}
                                  <tr
                                    onClick={() => this.setState(prev => ({ psOpenMods: { ...prev.psOpenMods, [modKey]: !modOpen } }))}
                                    style={{ cursor: "pointer", background: "#f4f5f7", borderBottom: "1px solid #e8e8e8" }}
                                  >
                                    <td style={{ padding: "8px 10px" }}>
                                      <Icon style={{ fontSize: "16px", color: "#666" }}>{modOpen ? "expand_more" : "chevron_right"}</Icon>
                                    </td>
                                    <td colSpan={3} style={{ padding: "8px 10px", fontWeight: 700, fontSize: "13px", color: "#3C4858" }}>
                                      Module: {modGroup.label}
                                    </td>
                                  </tr>
                                  {modOpen && Object.entries(modGroup.subs).map(([subKey, subGroup]) => {
                                    const subOpen = psOpenSubs[`${modKey}.${subKey}`] !== false;
                                    return (
                                      <React.Fragment key={subKey}>
                                        {/* Sub module row */}
                                        <tr
                                          onClick={() => this.setState(prev => ({ psOpenSubs: { ...prev.psOpenSubs, [`${modKey}.${subKey}`]: !subOpen } }))}
                                          style={{ cursor: "pointer", background: "#fafafa", borderBottom: "1px solid #eee" }}
                                        >
                                          <td style={{ padding: "7px 10px 7px 28px" }}>
                                            <Icon style={{ fontSize: "15px", color: "#999" }}>{subOpen ? "expand_more" : "chevron_right"}</Icon>
                                          </td>
                                          <td colSpan={3} style={{ padding: "7px 10px", fontWeight: 600, fontSize: "13px", color: "#555" }}>
                                            Sub Module: {subGroup.label}
                                          </td>
                                        </tr>
                                        {/* Action rows */}
                                        {subOpen && subGroup.entries.map(entry => (
                                          <tr key={entry.id} style={{ borderBottom: "1px solid #f4f4f4" }}>
                                            <td style={{ padding: "7px 10px 7px 48px" }}></td>
                                            <td style={{ padding: "7px 10px", fontSize: "13px", color: "#444" }}>{entry.process}</td>
                                            <td style={{ padding: "7px 10px", fontSize: "13px", color: "#444" }}>{entry.permission}</td>
                                            <td style={{ padding: "7px 10px" }}>
                                              <span
                                                style={{ fontSize: "12px", fontWeight: 700, color: "#e53935", cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.4px" }}
                                                onClick={() => this.psRemoveEntry(entry.id)}
                                              >REMOVE</span>
                                            </td>
                                          </tr>
                                        ))}
                                      </React.Fragment>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* RIGHT: add panel */}
                      {psShowPanel && (
                        <div className={classes.card} style={{ width: "340px", flexShrink: 0, padding: "20px" }}>
                          <p style={{ margin: "0 0 18px", fontSize: "14px", fontWeight: 700, color: "#3C4858", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            ADD MODULE PERMISSION
                          </p>

                          <div style={{ marginBottom: "20px" }}>
                            <SelectSearchSingle
                              label="Module"
                              placeholder="Select Module"
                              name="psNewModule"
                              value={psNewModule || null}
                              suggestions={MODULE_TREE.map(m => ({ value: m.key, label: m.label }))}
                              getSearchValue={val => this.setState({ psNewModule: val, psNewSub: null, psNewProcess: null })}
                            />
                          </div>

                          <div style={{ marginBottom: "20px" }}>
                            <SelectSearchSingle
                              label="Sub Module"
                              placeholder="Select Sub Module"
                              name="psNewSub"
                              value={psNewSub || null}
                              suggestions={psNewModule ? getSubmodules(psNewModule).map(s => ({ value: s.key, label: s.label })) : []}
                              getSearchValue={val => this.setState({ psNewSub: val, psNewProcess: null })}
                            />
                          </div>

                          <div style={{ marginBottom: "20px" }}>
                            <SelectSearchSingle
                              label="Process"
                              placeholder="Select Process"
                              name="psNewProcess"
                              value={psNewProcess || null}
                              suggestions={subActions.map(a => ({ value: a, label: a }))}
                              getSearchValue={val => this.setState({ psNewProcess: val })}
                            />
                          </div>

                          <div style={{ marginBottom: "24px" }}>
                            <SelectSearchSingle
                              label="Select Permission(s)"
                              placeholder="Read Only / Read Write / Full Rights"
                              name="psNewPermission"
                              value={psNewPermission || null}
                              suggestions={PERMISSION_LEVELS.map(p => ({ value: p, label: p }))}
                              getSearchValue={val => this.setState({ psNewPermission: val })}
                            />
                          </div>

                          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", paddingTop: "4px", borderTop: "1px solid #f0f0f0" }}>
                            <button
                              className={classes.btnOutlined}
                              onClick={() => this.setState({ psShowPanel: false, psNewModule: null, psNewSub: null, psNewProcess: null, psNewPermission: null })}
                            >
                              CLOSE
                            </button>
                            <button
                              className={classes.btnFilled}
                              onClick={this.psAddEntry}
                              disabled={!psNewModule || !psNewSub || !psNewProcess || !psNewPermission}
                              style={(!psNewModule || !psNewSub || !psNewProcess || !psNewPermission) ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                            >
                              SAVE SET
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* ── TAB 2: PERMISSION MATRIX ── */}
            {tab === 2 && (
              <>
                <div className={classes.actionRow}>
                  <button
                    className={classes.btnFilled}
                    disabled={!matrixDirty}
                    onClick={() => this.setState({ matrixDirty: false, snack: true, snackMsg: "Matrix saved.", snackOk: true })}
                    style={!matrixDirty ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                  >
                    Save Matrix
                  </button>
                </div>

                <div className={classes.card} style={{ padding: "20px 24px" }}>
                  <div className={classes.infoBar}>
                    <Icon className={classes.infoIcon} style={{ fontSize: "18px" }}>info</Icon>
                    <span>Role-based access overview. Click a cell to cycle: <strong>Full Access</strong> → <strong>View Only</strong> → <strong>No Access</strong>.</span>
                  </div>

                  <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", margin: "0 0 16px", fontSize: "12px" }}>
                    <span><Checkbox checked style={{ padding: "2px", color: NAVY }} size="small" /> Full Access</span>
                    <span><Checkbox indeterminate style={{ padding: "2px", color: NAVY }} size="small" /> View Only</span>
                    <span><Checkbox style={{ padding: "2px" }} size="small" /> No Access</span>
                  </div>

                  <div className={classes.matrixWrap}>
                    <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "780px" }}>
                      <thead>
                        <tr>
                          <th className={`${classes.matrixTh} ${classes.matrixThFirst}`} style={{ textAlign: "left" }}>Module</th>
                          {ROLES.map(r => <th key={r} className={classes.matrixTh}>{r}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(matrix).map((mod, idx) => (
                          <tr key={mod} style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                            <td className={classes.matrixTdFirst}>{mod}</td>
                            {ROLES.map(role => {
                              const val = matrix[mod][role];
                              return (
                                <td key={role} className={classes.matrixTd}>
                                  <Tooltip title={val === true ? "Full Access" : val === "view" ? "View Only" : "No Access"}>
                                    <Checkbox
                                      checked={val === true}
                                      indeterminate={val === "view"}
                                      onChange={() => this.cycleMatrix(mod, role)}
                                      style={{ padding: "4px", color: val === true ? NAVY : val === "view" ? "#e65100" : undefined }}
                                      size="small"
                                    />
                                  </Tooltip>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

          </GridItem>
        </GridContainer>

        <Snackbar
          open={snack}
          message={snackMsg}
          autoHideDuration={3500}
          onClose={() => this.setState({ snack: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          ContentProps={{ style: { background: snackOk ? "#388e3c" : "#d32f2f" } }}
        />
      </div>
    );
  }
}

PermissionsMgt.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(PermissionsMgt));
